import React, { useEffect, useRef } from 'react';
import { Box, Typography, Button, Container, Chip } from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VerifiedIcon from '@mui/icons-material/Verified';

const stats = [
  { num: '500+', label: 'Installations' },
  { num: '15+', label: 'Years Experience' },
  { num: '24/7', label: 'Support' },
  { num: '99.9%', label: 'Uptime' },
];

export default function Hero() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animId;
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Particle grid effect
    const particles = [];
    for (let i = 0; i < 80; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        size: Math.random() * 2 + 0.5,
        opacity: Math.random() * 0.5 + 0.1,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(30, 144, 212, ${p.opacity})`;
        ctx.fill();
      });

      // Draw lines between close particles
      particles.forEach((p1, i) => {
        particles.slice(i + 1).forEach((p2) => {
          const dist = Math.hypot(p1.x - p2.x, p1.y - p2.y);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `rgba(30, 144, 212, ${0.12 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.8;
            ctx.stroke();
          }
        });
      });

      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);

  return (
    <Box
      id="home"
      sx={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #061525 0%, #0a2540 40%, #0d1e35 100%)',
      }}
    >
      {/* Animated canvas */}
      <canvas ref={canvasRef} style={{
        position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none'
      }} />

      {/* Glowing orbs */}
      <Box sx={{
        position: 'absolute', width: 600, height: 600,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(30,144,212,0.12) 0%, transparent 70%)',
        top: -150, right: -100, zIndex: 0,
        animation: 'pulse 4s ease-in-out infinite',
        '@keyframes pulse': {
          '0%, 100%': { transform: 'scale(1)', opacity: 0.8 },
          '50%': { transform: 'scale(1.1)', opacity: 1 },
        }
      }} />
      <Box sx={{
        position: 'absolute', width: 400, height: 400,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(30,144,212,0.08) 0%, transparent 70%)',
        bottom: 100, left: -100, zIndex: 0,
      }} />

      {/* Scanning line animation */}
      <Box sx={{
        position: 'absolute', inset: 0, zIndex: 1, overflow: 'hidden', pointerEvents: 'none',
        '&::after': {
          content: '""',
          position: 'absolute', left: 0, right: 0, height: '2px',
          background: 'linear-gradient(90deg, transparent, rgba(30,144,212,0.4), transparent)',
          animation: 'scan 6s linear infinite',
          '@keyframes scan': {
            '0%': { top: 0, opacity: 0 },
            '5%': { opacity: 1 },
            '95%': { opacity: 1 },
            '100%': { top: '100%', opacity: 0 },
          }
        }
      }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, pt: 12, pb: 8 }}>
        <Box sx={{ maxWidth: 750 }}>
          {/* Badge */}
          <Chip
            icon={<VerifiedIcon sx={{ fontSize: '0.9rem !important', color: '#1e90d4 !important' }} />}
            label="Trusted Security Partner Since 2009"
            sx={{
              mb: 4, background: 'rgba(30,144,212,0.12)',
              border: '1px solid rgba(30,144,212,0.3)',
              color: '#8fb3d9', fontSize: '0.8rem',
              animation: 'fadeInDown 0.8s ease forwards',
              '@keyframes fadeInDown': {
                from: { opacity: 0, transform: 'translateY(-20px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              }
            }}
          />

          {/* Headline */}
         <Typography variant="h1" className="fade-in-1" sx={{ fontSize: { xs: 40, md: 64, lg: 72 }, lineHeight: 1.05, mb: 1 }}>
                CCTV
              </Typography>

              <Typography variant="h1" className="fade-in-2" sx={{ fontSize: { xs: 40, md: 64, lg: 72 }, lineHeight: 1.05, color: "#00c8ff", textShadow: "0 0 40px rgba(0,200,255,0.5)", mb: 2 }}>
                PARTNER
              </Typography>

               <Typography className="fade-in-2" sx={{ fontFamily: "'Rajdhani',sans-serif", fontSize: { xs: 16, md: 20 }, fontWeight: 600, color: "#f0a500", letterSpacing: "5px", textTransform: "uppercase", mb: 3 }}>
                Eagle Eye Surveillance System
              </Typography>

          <Typography sx={{
            fontSize: '1.15rem', color: '#8fb3d9', lineHeight: 1.8,
            maxWidth: 560, mb: 5,
            animation: 'fadeInUp 0.8s ease 0.4s both',
          }}>
            Professional CCTV installation, maintenance & monitoring. Protect your home, business,
            and loved ones with cutting-edge surveillance technology.
          </Typography>

          {/* CTA Buttons */}
          <Box sx={{
            display: 'flex', gap: 2, flexWrap: 'wrap', mb: 8,
            animation: 'fadeInUp 0.8s ease 0.6s both',
          }}>
            <Button
              variant="contained" size="large"
              startIcon={<SecurityIcon />}
              href="#contact"
              onClick={(e) => { e.preventDefault(); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }}
              sx={{
                background: 'linear-gradient(135deg, #1e90d4 0%, #0a5a8a 100%)',
                px: 4, py: 1.8, fontSize: '1rem',
                boxShadow: '0 8px 32px rgba(30,144,212,0.45)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 40px rgba(30,144,212,0.6)',
                },
                transition: 'all 0.3s',
              }}
            >
              Get Free Consultation
            </Button>
            <Button
              variant="outlined" size="large"
              startIcon={<PlayArrowIcon />}
              sx={{
                borderColor: 'rgba(30,144,212,0.4)', color: '#8fb3d9',
                px: 4, py: 1.8,
                '&:hover': {
                  borderColor: '#1e90d4', color: '#e8f0fe',
                  background: 'rgba(30,144,212,0.08)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s',
              }}
            >
              View Our Work
            </Button>
          </Box>

          {/* Stats */}
          <Box sx={{
            display: 'flex', gap: { xs: 3, md: 5 }, flexWrap: 'wrap',
            animation: 'fadeInUp 0.8s ease 0.8s both',
          }}>
            {stats.map((s, i) => (
              <Box key={i}>
                <Typography sx={{
                  fontSize: '2rem', fontWeight: 700, color: '#1e90d4',
                  fontFamily: '"Playfair Display", serif',
                  lineHeight: 1,
                }}>
                  {s.num}
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#8fb3d9', letterSpacing: '1px', textTransform: 'uppercase' }}>
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Floating camera card */}
        <Box sx={{
          position: { md: 'absolute' }, right: { md: 60 }, top: { md: '50%' },
          transform: { md: 'translateY(-50%)' },
          mt: { xs: 6, md: 0 },
          display: { xs: 'none', lg: 'block' },
          animation: 'float 4s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(-50%) translateX(0)' },
            '50%': { transform: 'translateY(-55%) translateX(-8px)' },
          }
        }}>
          <Box sx={{
            width: 280,
            background: 'rgba(13, 40, 71, 0.8)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(30,144,212,0.25)',
            borderRadius: '16px',
            p: 3,
            boxShadow: '0 24px 60px rgba(0,0,0,0.5), 0 0 40px rgba(30,144,212,0.1)',
          }}>
            {/* Mock camera feed */}
            <Box sx={{
              width: '100%', height: 160,
              background: 'linear-gradient(135deg, #050f1a, #0a1e30)',
              borderRadius: '8px', mb: 2, position: 'relative', overflow: 'hidden',
              border: '1px solid rgba(30,144,212,0.15)',
            }}>
              <Box sx={{
                position: 'absolute', top: 8, left: 8,
                background: 'rgba(255,60,60,0.9)', borderRadius: '50%',
                width: 8, height: 8,
                animation: 'blink 1.5s ease infinite',
                '@keyframes blink': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.2 },
                }
              }} />
              <Typography sx={{ position: 'absolute', top: 6, left: 22, fontSize: '0.6rem', color: '#8fb3d9', letterSpacing: 1 }}>
                CAM-01 • LIVE
              </Typography>
              <Typography sx={{ position: 'absolute', bottom: 8, right: 8, fontSize: '0.6rem', color: '#1e90d4', fontFamily: 'monospace' }}>
                10:42:33
              </Typography>
              {/* Grid lines */}
              {[...Array(4)].map((_, i) => (
                <Box key={i} sx={{
                  position: 'absolute', left: 0, right: 0,
                  top: `${25 * (i + 1)}%`, height: '1px',
                  background: 'rgba(30,144,212,0.08)',
                }} />
              ))}
              {[...Array(3)].map((_, i) => (
                <Box key={i} sx={{
                  position: 'absolute', top: 0, bottom: 0,
                  left: `${33 * (i + 1)}%`, width: '1px',
                  background: 'rgba(30,144,212,0.08)',
                }} />
              ))}
              {/* Corner brackets */}
              {[
                { top: 20, left: 20 }, { top: 20, right: 20 },
                { bottom: 20, left: 20 }, { bottom: 20, right: 20 }
              ].map((pos, i) => (
                <Box key={i} sx={{
                  position: 'absolute', width: 14, height: 14,
                  ...pos,
                  borderTop: i < 2 ? '2px solid #1e90d4' : 'none',
                  borderBottom: i >= 2 ? '2px solid #1e90d4' : 'none',
                  borderLeft: i % 2 === 0 ? '2px solid #1e90d4' : 'none',
                  borderRight: i % 2 === 1 ? '2px solid #1e90d4' : 'none',
                }} />
              ))}
            </Box>
            <Typography sx={{ fontSize: '0.85rem', color: '#e8f0fe', fontWeight: 600, mb: 1 }}>
              Live Monitoring Active
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#4caf50', animation: 'blink 2s infinite' }} />
              <Typography sx={{ fontSize: '0.75rem', color: '#8fb3d9' }}>
                All cameras operational
              </Typography>
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Bottom gradient fade */}
      <Box sx={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: 120,
        background: 'linear-gradient(to top, #081e35, transparent)',
        zIndex: 2,
      }} />
    </Box>
  );
}
